/**
 * Following a stint plan while the race is running.
 *
 * Three jobs, all pure so they can be tested without a socket:
 *
 *  1. `raceClock` — how many race minutes have gone, from the session countdown.
 *  2. `observeTeam` — notice when each stint really started and ended, from the
 *     fields Apex already sends. Keyed by the team's pit count, and every reading
 *     only ever moves a value *down*, so seeing the same frame twice (React's
 *     double render, a replay seeking backwards) records nothing new.
 *  3. `reconcile` — lay what happened over the plan. Finished stints become what
 *     they really were, the stint in the kart is anchored to its real start, and
 *     the stints still to come are rebalanced so the race ends at the flag with
 *     the same number of stops.
 *
 * The reconciled result is itself a `StrategyPlan`, so the validator, the totals
 * and the stint grid work on it unchanged.
 */
import { PlannedStint, StintActual, StopKind, StrategyActuals, StrategyPlan } from '../types';
import { deriveConstraints } from './regulations';
import { normalizeDriverLabel } from './driveTime';
import { pitElapsedSeconds, stintMinutes } from './lapTime';
import { PlanSummary, Violation, rebalanceTail, summarisePlan, validatePlan } from './stintPlan';

/** Minutes below which a lowered reading is not worth recording again. */
const MIN_STEP = 0.25;

/** Slack before the plan is said to miss the flag, in minutes. */
const END_TOLERANCE = 0.5;

// ── Clock ──────────────────────────────────────────────────────────────────────

export interface RaceClock {
  /** Race minutes gone. */
  elapsedMinutes: number;
  /**
   * `countdown` is Apex's own session timer and is what actuals are recorded
   * against. `estimated` is the planned start time against the wall clock: good
   * enough to show a plan moving, not good enough to write down what happened.
   */
  source: 'countdown' | 'estimated';
}

/**
 * Race minutes gone.
 *
 * The countdown is preferred because it is in race time: it does not care that a
 * 25-hour race crosses midnight twice, or that the start was a few minutes late.
 * A countdown that claims more time left than the whole race is a different
 * session's (qualifying, say) and is ignored.
 */
export function raceClock(
  config: { durationHours: number; startsAt: string },
  sessionEndsAt: number | null,
  now: number,
): RaceClock | null {
  const raceMinutes = config.durationHours * 60;

  if (sessionEndsAt !== null) {
    const remaining = (sessionEndsAt - now) / 60_000;
    if (remaining <= raceMinutes + 1) {
      return { elapsedMinutes: Math.max(0, raceMinutes - remaining), source: 'countdown' };
    }
  }

  const start = Date.parse(config.startsAt);
  if (!Number.isFinite(start)) return null;
  return { elapsedMinutes: Math.max(0, (now - start) / 60_000), source: 'estimated' };
}

// ── Observation ────────────────────────────────────────────────────────────────

/** The fields of a live team that say where it is in its stint. */
export interface LiveTeamState {
  driver: string;
  pitCount: string;
  inBox: boolean;
  onTrackTime: string;
}

/** `teamId -> stint index -> what has been seen of it`. */
export type ObservationStore = Map<string, Map<number, StintActual>>;

export function emptyObservations(): ObservationStore {
  return new Map();
}

/** Stops the team has made, which is also the index of the stint it is in. */
export function stopsMade(team: { pitCount: string }): number {
  const n = parseInt(team.pitCount, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Fold the current frame into the store; returns just what changed.
 *
 * Apex bumps the pit count when a kart enters the lane, so a kart on track with
 * count N is in stint N, and a boxed kart with count N is in the stop that ended
 * stint N-1.
 *
 * Readings are minimums on purpose. The stint clock only has minute resolution
 * and floors, so `elapsed - age` reads up to a minute late until the clock ticks
 * over; the smallest value seen is the closest to the truth. A value must drop by
 * a quarter minute to count as news, which keeps clock jitter from producing a
 * write per frame.
 */
export function observeTeam(
  store: ObservationStore,
  teamId: string,
  team: LiveTeamState,
  clock: RaceClock,
): StrategyActuals {
  const stops = stopsMade(team);
  const byStint = store.get(teamId) ?? new Map<number, StintActual>();
  store.set(teamId, byStint);

  const patch: StrategyActuals = {};
  const note = (index: number, change: StintActual): void => {
    patch[String(index)] = { ...patch[String(index)], ...change };
  };

  const lower = (index: number, field: 'startMinute' | 'pitInMinute', value: number): void => {
    const record = byStint.get(index) ?? {};
    const seen = record[field];
    const next = Math.max(0, value);
    if (seen !== undefined && next > seen - MIN_STEP) return;
    record[field] = round2(next);
    byStint.set(index, record);
    note(index, { [field]: record[field] });
  };

  if (!team.inBox) {
    // Stint 0 starts when the race does; there is nothing to measure.
    const age = stintMinutes(team.onTrackTime);
    if (stops > 0 && age !== null) lower(stops, 'startMinute', clock.elapsedMinutes - age);

    const name = normalizeDriverLabel(team.driver);
    if (name) {
      const record = byStint.get(stops) ?? {};
      if (record.driver !== name) {
        record.driver = name;
        byStint.set(stops, record);
        note(stops, { driver: name });
      }
    }
  } else {
    // The driver on the row changes while the kart is still in the lane, so the
    // name is not read until it is back on track.
    const seconds = pitElapsedSeconds(team.onTrackTime);
    if (stops > 0 && seconds !== null) {
      lower(stops - 1, 'pitInMinute', clock.elapsedMinutes - seconds / 60);
    }
  }

  return patch;
}

/**
 * The part of a finished race's record that had already happened when the team was
 * in stint `stops`. A replay that has been seeked past some stops never watched
 * them go by, but the stored record knows how they went, and they are in the past
 * however far back the playhead is moved. Anything about the stint in progress
 * beyond its start, or any later stint, would be reading the future, so it is left out.
 */
export function actualsUpTo(
  actuals: StrategyActuals | null | undefined,
  stops: number,
): StrategyActuals {
  const out: StrategyActuals = {};
  for (const [key, value] of Object.entries(actuals ?? {})) {
    const index = Number(key);
    if (index < stops) {
      out[key] = { ...value };
    } else if (index === stops) {
      const known: StintActual = {};
      if (value.startMinute !== undefined) known.startMinute = value.startMinute;
      if (value.driver !== undefined) known.driver = value.driver;
      out[key] = known;
    }
  }
  return out;
}

/** Everything the store holds for a team, in the shape the server keeps. */
export function observedActuals(store: ObservationStore, teamId: string): StrategyActuals {
  const out: StrategyActuals = {};
  store.get(teamId)?.forEach((record, index) => {
    out[String(index)] = { ...record };
  });
  return out;
}

/**
 * Combine two sets of actuals. Mirrors the server's merge: times take the earlier
 * reading, the driver takes the later one, and merging the same thing twice
 * changes nothing.
 */
export function mergeActuals(
  existing: StrategyActuals | null | undefined,
  patch: StrategyActuals | null | undefined,
): StrategyActuals {
  const out: StrategyActuals = {};
  for (const [key, value] of Object.entries(existing ?? {})) out[key] = { ...value };

  for (const [key, incoming] of Object.entries(patch ?? {})) {
    const current: StintActual = out[key] ?? {};
    const merged: StintActual = { ...current };
    if (incoming.startMinute !== undefined) {
      merged.startMinute =
        current.startMinute === undefined
          ? incoming.startMinute
          : Math.min(current.startMinute, incoming.startMinute);
    }
    if (incoming.pitInMinute !== undefined) {
      merged.pitInMinute =
        current.pitInMinute === undefined
          ? incoming.pitInMinute
          : Math.min(current.pitInMinute, incoming.pitInMinute);
    }
    if (incoming.driver !== undefined) merged.driver = incoming.driver;
    out[key] = merged;
  }
  return out;
}

/**
 * What `observed` knows that `known` does not yet: a time recorded for the first
 * time, a time lower by more than the noise floor, or a different driver. This is
 * what a browser still has to send. Empty means the server is up to date.
 */
export function diffActuals(
  observed: StrategyActuals,
  known: StrategyActuals | null | undefined,
): StrategyActuals {
  const out: StrategyActuals = {};
  for (const [key, seen] of Object.entries(observed)) {
    const have = known?.[key];
    const change: StintActual = {};
    for (const field of ['startMinute', 'pitInMinute'] as const) {
      const value = seen[field];
      if (value === undefined) continue;
      const stored = have?.[field];
      if (stored === undefined || value < stored - MIN_STEP) change[field] = value;
    }
    if (seen.driver !== undefined && seen.driver !== have?.driver) change.driver = seen.driver;
    if (Object.keys(change).length > 0) out[key] = change;
  }
  return out;
}

// ── Matching ───────────────────────────────────────────────────────────────────

/**
 * Names as the feed spells them and as a person typed them rarely agree on case,
 * accents or word order ("PETROV Ivan" against "Ivan Petrov"), so compare the
 * set of words rather than the string.
 */
function nameKey(name: string): string {
  return normalizeDriverLabel(name)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .sort()
    .join(' ');
}

/** Index of the roster entry the feed's driver label refers to, or null. */
export function matchDriver(roster: { name: string }[], label: string): number | null {
  const key = nameKey(label);
  if (!key) return null;
  const index = roster.findIndex((d) => nameKey(d.name) === key);
  return index === -1 ? null : index;
}

/**
 * Which live team a plan runs for. The grid row id is exact within one event;
 * the kart number is only a fallback for when the id means nothing (a plan reused
 * for a new event), because karts get swapped mid-race and the number moves with
 * them.
 */
export function matchStrategyToTeam<T extends { kartNumber: string }>(
  strategy: { teamId: string | null; kartNumber: string | null },
  teams: Map<string, T>,
): string | null {
  if (strategy.teamId && teams.has(strategy.teamId)) return strategy.teamId;
  const kart = strategy.kartNumber?.trim();
  if (!kart) return null;
  const hits = Array.from(teams.entries()).filter(([, t]) => t.kartNumber.trim() === kart);
  return hits.length === 1 ? hits[0][0] : null;
}

// ── Reconciliation ─────────────────────────────────────────────────────────────

export type LiveStatus = 'done' | 'done-unobserved' | 'running' | 'boxed' | 'upcoming';

export interface LiveStint {
  status: LiveStatus;
  /** What the saved plan said, before the race changed it. */
  plannedStartMinute: number;
  plannedMinutes: number;
  plannedDriverIndex: number | null;
  /** The driver label the feed showed, when one was seen. */
  actualDriver: string | null;
  /**
   * Done: how much longer (+) or shorter (−) it really was. Running and
   * upcoming: how far the rebalance has moved it. Null when nothing was measured.
   */
  deltaMinutes: number | null;
}

export interface NextStop {
  /** The stint that ends with this stop. */
  stintIndex: number;
  /**
   * Race minute the plan wants the Pit In. This is what the prompts count down
   * to, and it stays put when the stint overruns, so `inMinutes` can go negative.
   */
  atMinute: number;
  /** Minutes from now to `atMinute`; negative once the stop is overdue. */
  inMinutes: number;
  /**
   * Race minute the sheet now projects the Pit In for. A running stint cannot be
   * planned to end in the past, so once it overruns this slides along with the
   * clock while `atMinute` does not.
   */
  projectedAtMinute: number;
  stopKind: StopKind;
  /** The driver in the kart now (as the feed shows them), if known. */
  outDriver: string | null;
  /** Who is planned to take over. */
  nextDriver: string | null;
  /** False when somebody other than the planned driver is in the kart. */
  currentDriverIsPlanned: boolean;
}

export interface LivePlan {
  /** The plan with the race laid over it. Safe to hand to the editor, validator and grid. */
  plan: StrategyPlan;
  stints: LiveStint[];
  /** The stint in the kart, or the one about to start while boxed. */
  currentIndex: number;
  boxed: boolean;
  clock: RaceClock;
  nextStop: NextStop | null;
  violations: Violation[];
  summary: PlanSummary;
  /** Things worth saying that are not rule breaches. */
  warnings: string[];
}

function driverName(plan: StrategyPlan, index: number | null): string | null {
  return index === null ? null : (plan.roster[index]?.name ?? null);
}

/** Pad a baseline out to `length` when the team has made more stops than planned. */
function extendBaseline(
  stints: PlannedStint[],
  length: number,
  minPitMinutes: number,
): PlannedStint[] {
  const out = [...stints];
  while (out.length < length) {
    const prev = out[out.length - 1];
    out.push({
      startMinute: prev.startMinute + prev.minutes + minPitMinutes,
      minutes: prev.minutes,
      driverIndex: prev.driverIndex,
      stopKind: 'driver-and-kart',
      note: null,
    });
  }
  return out;
}

/**
 * Lay what happened over a plan.
 *
 * `actuals` is everything recorded so far, the server's and this browser's
 * merged. The live fields of `team` fill in the stint in progress even before
 * anything has been recorded for it.
 */
export function reconcile(
  plan: StrategyPlan,
  actuals: StrategyActuals,
  team: LiveTeamState,
  clock: RaceClock,
): LivePlan {
  const available = plan.roster.filter((d) => !d.unavailable).length;
  const c = deriveConstraints(plan.config, available);
  const elapsed = clock.elapsedMinutes;
  const warnings: string[] = [];

  if (plan.stints.length === 0) {
    return {
      plan,
      stints: [],
      currentIndex: 0,
      boxed: team.inBox,
      clock,
      nextStop: null,
      violations: validatePlan(plan),
      summary: summarisePlan(plan),
      warnings,
    };
  }

  const stops = stopsMade(team);
  if (stops >= plan.stints.length) {
    warnings.push(
      `${stops} stops made; the plan only had ${plan.stints.length - 1}. ` +
        'The extra stint is a copy of the last one.',
    );
  }
  const base = extendBaseline(plan.stints, stops + 1, c.minPitMinutes);
  const live: LiveStint[] = [];

  // ── Stints already run ──────────────────────────────────────────────────────
  const done: PlannedStint[] = [];
  let unobserved = 0;
  for (let i = 0; i < stops; i += 1) {
    const planned = base[i];
    const seen = actuals[String(i)] ?? {};
    const following = actuals[String(i + 1)];
    const prev = done[i - 1];

    const start =
      i === 0
        ? 0
        : (seen.startMinute ??
          (prev ? prev.startMinute + prev.minutes + c.minPitMinutes : planned.startMinute));
    const pitIn =
      seen.pitInMinute ??
      (following?.startMinute !== undefined ? following.startMinute - c.minPitMinutes : undefined);
    const measured = pitIn !== undefined && pitIn > start;

    const minutes = measured ? pitIn - start : planned.minutes;
    const matched = seen.driver ? matchDriver(plan.roster, seen.driver) : null;
    if (!measured) unobserved += 1;

    done.push({
      startMinute: start,
      minutes,
      // A driver the roster cannot place is assumed to be the planned one: the
      // alternative is losing their minutes from every total on a spelling.
      driverIndex: matched ?? planned.driverIndex,
      stopKind: planned.stopKind,
      note: planned.note,
    });
    live.push({
      status: measured ? 'done' : 'done-unobserved',
      plannedStartMinute: planned.startMinute,
      plannedMinutes: planned.minutes,
      plannedDriverIndex: planned.driverIndex,
      actualDriver: seen.driver ?? null,
      deltaMinutes: measured ? minutes - planned.minutes : null,
    });
  }
  if (unobserved > 0) {
    warnings.push(
      `${unobserved} earlier stint${unobserved === 1 ? ' was' : 's were'} not observed ` +
        '— they are shown as planned.',
    );
  }

  // ── The stint in the kart ───────────────────────────────────────────────────
  const planned = base[stops];
  const seenNow = actuals[String(stops)] ?? {};
  const boxed = team.inBox && stops > 0;

  let startMinute: number;
  let minLength = 0;
  let runningDriver = planned.driverIndex;
  let actualDriver: string | null = seenNow.driver ?? null;

  if (boxed) {
    // In the lane: the stint has not begun. It will start once this stop is over.
    const lastEnd = done.length > 0 ? done[done.length - 1].startMinute + done[done.length - 1].minutes : 0;
    startMinute = Math.max(lastEnd + c.minPitMinutes, elapsed);
  } else {
    const age = stintMinutes(team.onTrackTime);
    startMinute =
      stops === 0
        ? 0
        : (seenNow.startMinute ?? (age !== null ? Math.max(0, elapsed - age) : planned.startMinute));
    minLength = Math.max(0, elapsed - startMinute);

    const label = normalizeDriverLabel(team.driver);
    if (label) {
      actualDriver = label;
      const matched = matchDriver(plan.roster, label);
      if (matched !== null) runningDriver = matched;
    }
  }

  // ── The rest, rebalanced ────────────────────────────────────────────────────
  const rebalance = (length: number): PlannedStint[] =>
    rebalanceTail({
      config: plan.config,
      roster: plan.roster,
      done,
      running: {
        startMinute,
        minLength: length,
        plannedMinutes: planned.minutes,
        driverIndex: runningDriver,
        note: planned.note,
        pinned: planned.pinned,
      },
      tail: base.slice(stops + 1),
    });
  const stints = rebalance(minLength);

  // How long the plan wanted this stint to be, before the clock got in the way.
  // Only worth a second pass when the clock actually did: otherwise it is simply
  // the length just computed.
  const projectedLength = stints[stops].minutes;
  const dueLength =
    minLength > 0 && projectedLength - Math.ceil(minLength) < 0.01
      ? rebalance(0)[stops].minutes
      : projectedLength;

  live.push({
    status: boxed ? 'boxed' : 'running',
    plannedStartMinute: planned.startMinute,
    plannedMinutes: planned.minutes,
    plannedDriverIndex: planned.driverIndex,
    actualDriver,
    deltaMinutes: stints[stops].minutes - planned.minutes,
  });
  for (let i = stops + 1; i < stints.length; i += 1) {
    live.push({
      status: 'upcoming',
      plannedStartMinute: base[i].startMinute,
      plannedMinutes: base[i].minutes,
      plannedDriverIndex: base[i].driverIndex,
      actualDriver: null,
      deltaMinutes: stints[i].minutes - base[i].minutes,
    });
  }

  const result: StrategyPlan = { ...plan, stints };

  // ── Next stop ───────────────────────────────────────────────────────────────
  let nextStop: NextStop | null = null;
  if (stops < stints.length - 1 && elapsed < c.raceMinutes) {
    const current = stints[stops];
    const atMinute = current.startMinute + dueLength;
    const matched = actualDriver && !boxed ? matchDriver(plan.roster, actualDriver) : null;
    nextStop = {
      stintIndex: stops,
      atMinute,
      inMinutes: atMinute - elapsed,
      projectedAtMinute: current.startMinute + current.minutes,
      stopKind: current.stopKind,
      outDriver: boxed ? null : (actualDriver ?? driverName(result, current.driverIndex)),
      nextDriver: driverName(result, stints[stops + 1].driverIndex),
      // Only a *known other* driver counts as off-plan; a name the roster cannot
      // place is not evidence the plan is being ignored.
      currentDriverIsPlanned: matched === null || matched === planned.driverIndex,
    };
  }

  // ── Checks ──────────────────────────────────────────────────────────────────
  const violations = validatePlan(result).filter((v) => {
    // The sheet-adds-up check assumes every stop is the minimum length. Real stops
    // run longer, so it would complain after any slow stop. The end-of-race check
    // below is the one that matters, and it works in real minutes.
    if (v.rule === '3e') return false;
    // History is history: a margin warning on a stop already made helps nobody.
    if (v.severity === 'warning' && v.stintIndex !== null && v.stintIndex < stops) return false;
    return true;
  });

  const last = stints[stints.length - 1];
  const finish = last.startMinute + last.minutes;
  if (Math.abs(finish - c.raceMinutes) > END_TOLERANCE) {
    violations.push({
      stintIndex: stints.length - 1,
      rule: '3e',
      severity: finish > c.raceMinutes ? 'error' : 'warning',
      message:
        finish > c.raceMinutes
          ? `The remaining stops do not fit: the plan now runs ${Math.round(finish - c.raceMinutes)} min past the flag.`
          : `The plan now ends ${Math.round(c.raceMinutes - finish)} min before the flag.`,
      penalty: null,
    });
  }

  return {
    plan: result,
    stints: live,
    currentIndex: stops,
    boxed,
    clock,
    nextStop,
    violations,
    summary: summarisePlan(result),
    warnings,
  };
}

/** One line for a prompt: what has to change at this stop. */
export function describeStop(stop: NextStop): string {
  switch (stop.stopKind) {
    case 'driver-and-kart':
      return stop.nextDriver
        ? `change driver (${stop.nextDriver} in) and kart`
        : 'change driver and kart';
    case 'kart-only':
      return 'change kart only — same driver';
    default:
      return 'no change planned';
  }
}
